import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { FeatureGatingProvider } from "@/contexts/FeatureGatingContext";
import { HotelProvider } from "@/contexts/HotelContext";
import { GlobalLiveRegion, RouteChangeAnnouncer } from "@/hooks/useAccessibility";

// Eagerly loaded pages (needed immediately)
import Login from "@/pages/Login";
import LandingPage from "@/pages/LandingPage";

// Lazy loaded pages (code splitting)
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const RoomTypes = lazy(() => import("@/pages/RoomTypes"));
const Rooms = lazy(() => import("@/pages/Rooms"));
const Guests = lazy(() => import("@/pages/Guests"));
const Reservations = lazy(() => import("@/pages/Reservations"));
const Housekeeping = lazy(() => import("@/pages/Housekeeping"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const OTAIntegration = lazy(() => import("@/pages/OTAIntegration"));
const Reports = lazy(() => import("@/pages/Reports"));
const QuickBooking = lazy(() => import("@/pages/QuickBooking"));
const Settings = lazy(() => import("@/pages/Settings"));
const AmountDueReport = lazy(() => import("@/pages/AmountDueReport"));
const PoliceReport = lazy(() => import("@/pages/PoliceReport"));
const CFormSubmissions = lazy(() => import("@/pages/CFormSubmissions"));
const AdvanceBooking = lazy(() => import("@/pages/AdvanceBooking"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const ShiftBalance = lazy(() => import("@/pages/ShiftBalance"));
// SaaS Pages
const Pricing = lazy(() => import("@/pages/Pricing"));
const UsageDashboard = lazy(() => import("@/pages/UsageDashboard"));
const PaymentHistory = lazy(() => import("@/pages/PaymentHistory"));
const DemoRegister = lazy(() => import("@/pages/DemoRegister"));
const AdminDemoManagement = lazy(() => import("@/pages/AdminDemoManagement"));
const ReferralDashboard = lazy(() => import("@/pages/ReferralDashboard"));
const OwnerDashboard = lazy(() => import("@/pages/OwnerDashboard"));
// Auth Pages
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const JoinTeam = lazy(() => import("@/pages/JoinTeam"));
// Legal Pages
const TermsAndConditions = lazy(() => import("@/pages/legal/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("@/pages/legal/PrivacyPolicy"));
const CancellationRefund = lazy(() => import("@/pages/legal/CancellationRefund"));
const ShippingExchange = lazy(() => import("@/pages/legal/ShippingExchange"));
const ContactUs = lazy(() => import("@/pages/legal/ContactUs"));
// Public Portal
const PublicCFormPortal = lazy(() => import("@/pages/PublicCFormPortal"));
const CFormAuthCallback = lazy(() => import("@/pages/CFormAuthCallback"));
// CFormCompleteProfile removed - functionality merged into CFormPortalDashboard
const CFormPortalDashboard = lazy(() => import("@/pages/CFormPortalDashboard"));
const CFormPortalSubmit = lazy(() => import("@/pages/CFormPortalSubmit"));
const CFormPortalHistory = lazy(() => import("@/pages/CFormPortalHistory"));
// Police Portal
const PoliceLogin = lazy(() => import("@/pages/PoliceLogin"));
const PoliceDashboard = lazy(() => import("@/pages/PoliceDashboard"));
const PoliceReportDashboard = lazy(() => import("@/pages/police/PoliceReportDashboard"));
// Police Security Console (POLICE_SUPER_ADMIN only)
const PoliceSecurityLogin = lazy(() => import("@/pages/PoliceSecurityLogin"));
const PoliceSecurityDashboard = lazy(() => import("@/pages/PoliceSecurityDashboard"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Import token store for Safari/iOS fallback
import { getAccessToken, setAccessToken, clearAccessToken } from '@/utils/tokenStore';

// Enable cookie-based auth: all axios requests send httpOnly cookies
axios.defaults.withCredentials = true;

// Polyfill fetch to include credentials AND Authorization header for Safari fallback
const _originalFetch = window.fetch;
window.fetch = function(url, options = {}) {
  const urlStr = typeof url === 'string' ? url : (url instanceof Request ? url.url : String(url));
  if (urlStr.includes(BACKEND_URL) || urlStr.startsWith('/api')) {
    const headers = new Headers(options.headers || {});
    // Add Authorization header as fallback for Safari ITP cookie blocking
    const token = getAccessToken();
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return _originalFetch(url, { ...options, headers, credentials: options.credentials || 'include' });
  }
  return _originalFetch(url, options);
};

// Axios request interceptor: add Authorization header for Safari/iOS fallback
axios.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// --- Singleton Token Refresh Queue ---
// Prevents multiple concurrent refresh calls when several requests get 401 simultaneously.
// Only ONE refresh runs at a time; all other 401s wait for it.
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshComplete(newToken) {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

// Axios response interceptor to handle token expiry
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    
    // NEVER intercept auth endpoints — let login/register/refresh errors pass through directly
    const isAuthEndpoint = requestUrl.includes('/auth/login') || 
                           requestUrl.includes('/auth/register') || 
                           requestUrl.includes('/auth/refresh') ||
                           requestUrl.includes('/auth/logout') ||
                           requestUrl.includes('/demo/register');
    
    // NEVER intercept police portal endpoints — they have their own auth flow
    const isPoliceEndpoint = requestUrl.includes('/police/') || 
                             requestUrl.includes('/cform/portal/');
    
    if (isAuthEndpoint || isPoliceEndpoint) {
      return Promise.reject(error);
    }
    
    // If error is 401 (Unauthorized) and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      if (isRefreshing) {
        // Another refresh is already in progress — wait for it
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((newToken) => {
            if (newToken) {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              resolve(axios(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }
      
      // This request is the first to hit 401 — it owns the refresh
      isRefreshing = true;
      
      try {
        const refreshResponse = await axios.post(`${API}/auth/refresh`, {});
        
        if (refreshResponse.data.access_token) {
          const newToken = refreshResponse.data.access_token;
          // Store token for Safari/iOS fallback
          setAccessToken(newToken);
          // Notify all queued requests
          onRefreshComplete(newToken);
          isRefreshing = false;
          // Retry original request
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed — notify all queued requests to fail
        onRefreshComplete(null);
        isRefreshing = false;
        // Clear session and redirect to login
        localStorage.removeItem('user');
        localStorage.removeItem('hotel_context');
        localStorage.removeItem('is_demo');
        localStorage.removeItem('demo_status');
        clearAccessToken();
        // Clear server cookies too
        try { await axios.post(`${API}/auth/logout`); } catch (_) {}
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
      
      isRefreshing = false;
    }
    
    return Promise.reject(error);
  }
);

function ProtectedRoute({ children }) {
  const user = localStorage.getItem('user');
  return user ? children : <Navigate to="/home" />;
}

// Route that requires admin, owner, or application_owner role
function AdminOwnerRoute({ children }) {
  const userStr = localStorage.getItem('user');
  if (!userStr) return <Navigate to="/home" />;
  
  const user = JSON.parse(userStr);
  const userRole = user.role || '';
  const saasRole = user.saas_role || '';
  
  // Allow access for admin, owner, hotel_owner, and application_owner roles
  const allowedRoles = ['owner', 'admin', 'hotel_owner', 'application_owner'];
  const isAllowed = allowedRoles.includes(userRole) || 
                    saasRole === 'application_owner' || 
                    saasRole === 'platform_admin';
  
  if (!isAllowed) {
    // Redirect to dashboard if not allowed
    return <Navigate to="/" />;
  }
  
  return children;
}

// Role-based route guard — checks allowed roles (prevents URL-based RBAC bypass)
function RoleRoute({ children, allowedRoles = [] }) {
  const userStr = localStorage.getItem('user');
  if (!userStr) return <Navigate to="/home" />;
  
  const user = JSON.parse(userStr);
  const userRole = (user.role || '').toLowerCase();
  const saasRole = (user.saas_role || '').toLowerCase();
  
  // Application owner and admin always have full access
  const superRoles = ['owner', 'admin', 'hotel_owner', 'application_owner'];
  const isSuperRole = superRoles.includes(userRole) || 
                      saasRole === 'application_owner' || 
                      saasRole === 'platform_admin';
  
  if (isSuperRole) return children;
  
  // Check if user's role is in the allowed list
  const normalizedAllowed = allowedRoles.map(r => r.toLowerCase());
  // Also check aliases: front_desk → front_office
  const roleAliases = { 'front_desk': 'front_office', 'front_office': 'front_desk' };
  const isAllowed = normalizedAllowed.includes(userRole) || 
                    (roleAliases[userRole] && normalizedAllowed.includes(roleAliases[userRole]));
  
  if (!isAllowed) return <Navigate to="/" />;
  return children;
}

function PublicRoute({ children }) {
  const user = localStorage.getItem('user');
  return user ? <Navigate to="/" /> : children;
}

// Loading fallback component with accessibility
function PageLoader() {
  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-gray-50"
      role="status"
      aria-label="Loading page"
    >
      <div className="flex flex-col items-center gap-4">
        <div 
          className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"
          aria-hidden="true"
        ></div>
        <p className="text-gray-600 text-sm">Loading...</p>
        <span className="sr-only">Please wait while the page loads</span>
      </div>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
    <HotelProvider>
      <FeatureGatingProvider>
        <div className="App">
          <BrowserRouter>
            {/* Global accessibility live regions */}
            <GlobalLiveRegion />
            <RouteChangeAnnouncer />
            
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/home" element={<PublicRoute><LandingPage /></PublicRoute>} />
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/join" element={<JoinTeam />} />
                
                {/* Public C-Form Portal (Google OAuth) */}
                <Route path="/cform-portal" element={<PublicCFormPortal />} />
                <Route path="/cform-portal/callback" element={<CFormAuthCallback />} />
                <Route path="/cform-portal/dashboard" element={<CFormPortalDashboard />} />
                <Route path="/cform-portal/submit" element={<CFormPortalSubmit />} />
                <Route path="/cform-portal/history" element={<CFormPortalHistory />} />
                
                {/* Police Portal (separate auth) */}
                <Route path="/police/login" element={<PoliceLogin />} />
                <Route path="/police/dashboard" element={<PoliceDashboard />} />
                <Route path="/police/command-center" element={<PoliceReportDashboard />} />
                
                {/* Police Security Console (POLICE_SUPER_ADMIN only) */}
                <Route path="/admin/police-security" element={<PoliceSecurityLogin />} />
                <Route path="/admin/police-security/dashboard" element={<PoliceSecurityDashboard />} />
                
                {/* Protected Dashboard - redirects to /home if not authenticated */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                path="/room-types"
                element={
                  <RoleRoute allowedRoles={['owner', 'admin', 'manager', 'application_owner']}>
                    <RoomTypes />
                  </RoleRoute>
                }
              />
              <Route
                path="/rooms"
                element={
                  <RoleRoute allowedRoles={['owner', 'admin', 'manager', 'front_desk', 'front_office', 'housekeeping', 'staff', 'application_owner']}>
                    <Rooms />
                  </RoleRoute>
                }
              />
              <Route
                path="/guests"
                element={
                  <ProtectedRoute>
                    <Guests />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reservations"
                element={
                  <ProtectedRoute>
                    <Reservations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/housekeeping"
                element={
                  <ProtectedRoute>
                    <Housekeeping />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoices"
                element={
                  <ProtectedRoute>
                    <Invoices />
                  </ProtectedRoute>
                }
              />
              {/* OTA / Channel Manager - consolidated route */}
              <Route
                path="/ota-integration"
                element={
                  <ProtectedRoute>
                    <OTAIntegration />
                  </ProtectedRoute>
                }
              />
              {/* Redirect old channel-manager route */}
              <Route
                path="/channel-manager"
                element={<Navigate to="/ota-integration" replace />}
              />
              <Route
                path="/reports"
                element={
                  <RoleRoute allowedRoles={['owner', 'admin', 'manager', 'accountant', 'application_owner']}>
                    <Reports />
                  </RoleRoute>
                }
              />
              <Route
                path="/quick-booking"
                element={
                  <ProtectedRoute>
                    <QuickBooking />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <RoleRoute allowedRoles={['owner', 'admin', 'application_owner']}>
                    <Settings />
                  </RoleRoute>
                }
              />
              <Route
                path="/amount-due"
                element={
                  <ProtectedRoute>
                    <AmountDueReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/police-report"
                element={
                  <ProtectedRoute>
                    <PoliceReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cform-submissions"
                element={
                  <ProtectedRoute>
                    <CFormSubmissions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/advance-booking"
                element={
                  <ProtectedRoute>
                    <AdvanceBooking />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bulk-booking"
                element={
                  <ProtectedRoute>
                    <AdvanceBooking />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/expenses"
                element={
                  <ProtectedRoute>
                    <Expenses />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shift-balance"
                element={
                  <RoleRoute allowedRoles={['owner', 'admin', 'manager', 'accountant', 'front_desk', 'front_office', 'application_owner']}>
                    <ShiftBalance />
                  </RoleRoute>
                }
              />
              
              {/* SaaS Routes - Admin/Owner Only */}
              <Route 
                path="/pricing" 
                element={
                  <AdminOwnerRoute>
                    <Pricing />
                  </AdminOwnerRoute>
                } 
              />
              <Route
                path="/usage"
                element={
                  <AdminOwnerRoute>
                    <UsageDashboard />
                  </AdminOwnerRoute>
                }
              />
              <Route
                path="/payment-history"
                element={
                  <ProtectedRoute>
                    <PaymentHistory />
                  </ProtectedRoute>
                }
              />
              <Route path="/demo-register" element={<DemoRegister />} />
              <Route path="/demo/register" element={<DemoRegister />} />
              <Route path="/register" element={<DemoRegister />} />
              <Route
                path="/admin/demos"
                element={
                  <ProtectedRoute>
                    <AdminDemoManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/referrals"
                element={
                  <ProtectedRoute>
                    <ReferralDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/owner-dashboard"
                element={
                  <AdminOwnerRoute>
                    <OwnerDashboard />
                  </AdminOwnerRoute>
                }
              />
              
              {/* Legal Pages - Public Access */}
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/cancellation-refund" element={<CancellationRefund />} />
              <Route path="/shipping-exchange" element={<ShippingExchange />} />
              <Route path="/contact-us" element={<ContactUs />} />
              
              {/* Default redirect */}
              <Route path="*" element={<Navigate to="/home" />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </div>
      </FeatureGatingProvider>
    </HotelProvider>
    </LanguageProvider>
  );
}

export default App;
